import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings, Save, RefreshCw, Building2, TrendingUp, Clock, Zap, Edit3, RotateCcw, ImageIcon, Upload, Trash2, BadgeJapaneseYen } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useLogo } from '../../context/LogoContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AdminSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState({
    flat_fee_jpy: 500,
    markup_percent: 3.0,
  });
  const [marketRate, setMarketRate] = useState(null);
  const [manualRate, setManualRate] = useState('');
  const [isOverrideMode, setIsOverrideMode] = useState(false);
  const [bankInfo, setBankInfo] = useState({
    bank_name: '', branch_name: '', account_number: '',
    account_holder: '', account_type: '普通', notes: '',
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [hasCustomLogo, setHasCustomLogo] = useState(false);
  const [landingAmount, setLandingAmount] = useState(100000);
  const [savingLanding, setSavingLanding] = useState(false);
  const [heroTexts, setHeroTexts] = useState({
    ja: { title_1: '', title_2: '', title_3: '', desc: '' },
    pt: { title_1: '', title_2: '', title_3: '', desc: '' },
    en: { title_1: '', title_2: '', title_3: '', desc: '' },
  });
  const [editLang, setEditLang] = useState('ja');
  const fileInputRef = useRef(null);
  const { t } = useLanguage();
  const { logoUrl, refreshLogo } = useLogo();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [feeRes, rateRes] = await Promise.all([
        api.getFeeSettings(),
        axios.get(`${API_URL}/api/admin/settings/market-rate`, { headers: getAuthHeader() })
      ]);
      setSettings({
        flat_fee_jpy: feeRes.data.flat_fee_jpy ?? 500,
        markup_percent: feeRes.data.markup_percent ?? 3.0,
      });
      setMarketRate(rateRes.data);
      setIsOverrideMode(rateRes.data.source === 'manual');
      if (rateRes.data.rate) setManualRate(String(rateRes.data.rate));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
    try {
      const bankRes = await axios.get(`${API_URL}/api/admin/settings/bank-info`, { headers: getAuthHeader() });
      setBankInfo({
        bank_name: bankRes.data.bank_name || '', branch_name: bankRes.data.branch_name || '',
        account_number: bankRes.data.account_number || '', account_holder: bankRes.data.account_holder || '',
        account_type: bankRes.data.account_type || '普通', notes: bankRes.data.notes || '',
      });
    } catch (error) { console.error('Failed to fetch bank info:', error); }
    try {
      const logoRes = await axios.get(`${API_URL}/api/settings/logo`);
      setHasCustomLogo(!!logoRes.data.logo_url);
    } catch (error) { console.error('Failed to fetch logo:', error); }
    try {
      const landingRes = await axios.get(`${API_URL}/api/admin/settings/landing`, { headers: getAuthHeader() });
      setLandingAmount(landingRes.data.display_amount || 100000);
      if (landingRes.data.hero_texts) {
        setHeroTexts(prev => ({ ...prev, ...landingRes.data.hero_texts }));
      }
    } catch (error) { console.error('Failed to fetch landing settings:', error); }
    setLoading(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.error(t('admin_settings_logo_formats'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/admin/settings/logo`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('admin_settings_logo_success'));
      setHasCustomLogo(true);
      await refreshLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin_settings_logo_error'));
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setDeletingLogo(true);
    try {
      await axios.delete(`${API_URL}/api/admin/settings/logo`, { headers: getAuthHeader() });
      toast.success(t('admin_settings_logo_delete_success'));
      setHasCustomLogo(false);
      await refreshLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin_settings_logo_delete_error'));
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSaveLanding = async () => {
    if (!landingAmount || landingAmount <= 0) {
      toast.error(t('admin_settings_landing_invalid'));
      return;
    }
    setSavingLanding(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/landing`,
        { display_amount: parseInt(landingAmount), hero_texts: heroTexts },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      toast.success(t('admin_settings_landing_saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin_settings_landing_error'));
    } finally {
      setSavingLanding(false);
    }
  };

  const handleRefreshRate = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/settings/market-rate/refresh`, {}, { headers: getAuthHeader() });
      setMarketRate(prev => ({ ...prev, ...res.data }));
      setIsOverrideMode(false);
      toast.success(t('settings_rate_refreshed'));
    } catch (error) {
      toast.error(t('settings_rate_refresh_failed'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleOverrideRate = async () => {
    const rate = parseFloat(manualRate);
    if (!rate || rate <= 0) { toast.error(t('settings_rate_invalid')); return; }
    try {
      const res = await axios.post(`${API_URL}/api/admin/settings/market-rate/override`, { rate }, { headers: getAuthHeader() });
      setMarketRate(prev => ({ ...prev, ...res.data }));
      setIsOverrideMode(true);
      toast.success(t('settings_rate_overridden'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleEnableAutoRate = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/settings/market-rate/auto`, {}, { headers: getAuthHeader() });
      setMarketRate(prev => ({ ...prev, ...res.data }));
      setIsOverrideMode(false);
      toast.success(t('settings_rate_auto_enabled'));
    } catch (error) {
      toast.error('Error');
    }
  };

  const currentRate = marketRate?.rate || 0;
  const markup = settings.markup_percent / 100;
  const [simAmount, setSimAmount] = useState(100000);
  const finalRate = currentRate * (1 - markup);

  const preview = {
    amount_jpy: simAmount,
    fee_jpy: settings.flat_fee_jpy,
    total_jpy: simAmount + settings.flat_fee_jpy,
    exchange_rate: Math.round(finalRate * 100000) / 100000,
    amount_brl: Math.round(simAmount * finalRate * 100) / 100,
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateFeeSettings(settings);
      toast.success(t('settings_saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setSettings({ flat_fee_jpy: 500, markup_percent: 3.0 });

  const handleSaveBankInfo = async () => {
    setSavingBank(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/bank-info`, bankInfo, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success(t('settings_bank_saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings_bank_save_failed'));
    } finally {
      setSavingBank(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString();
  };

  if (loading) {
    return <AdminLayout><div className="text-center py-12 text-muted-foreground">{t('loading')}</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('admin_settings_title')}</h1>
          <p className="text-muted-foreground">{t('admin_settings_subtitle')}</p>
        </div>

        {/* Logo Upload Section */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-8" data-testid="logo-upload-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <ImageIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary">{t('admin_settings_logo_title')}</h2>
              <p className="text-sm text-muted-foreground">{t('admin_settings_logo_subtitle')}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-border bg-secondary/20 flex items-center justify-center overflow-hidden" data-testid="logo-preview">
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="w-full h-full object-contain p-1"
                  onError={(e) => { e.target.src = '/logo.jpg'; }}
                />
              </div>
              {!hasCustomLogo && (
                <p className="text-xs text-muted-foreground mt-2 text-center">{t('admin_settings_logo_default')}</p>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <p className="text-sm text-muted-foreground">{t('admin_settings_logo_formats')}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleLogoUpload}
                data-testid="logo-file-input"
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="logo-upload-btn"
                >
                  <Upload className="h-4 w-4" />
                  {uploadingLogo ? t('admin_settings_logo_uploading') : (hasCustomLogo ? t('admin_settings_logo_change') : t('admin_settings_logo_upload'))}
                </Button>
                {hasCustomLogo && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={handleLogoDelete}
                    disabled={deletingLogo}
                    data-testid="logo-delete-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                    {deletingLogo ? t('admin_settings_logo_deleting') : t('admin_settings_logo_delete')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Landing Page Display Amount */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-8" data-testid="landing-amount-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
              <BadgeJapaneseYen className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary">{t('admin_settings_landing_title')}</h2>
              <p className="text-sm text-muted-foreground">{t('admin_settings_landing_subtitle')}</p>
            </div>
          </div>
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1 max-w-xs">
              <Label>{t('admin_settings_landing_amount_label')}</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                <Input
                  type="number"
                  value={landingAmount}
                  onChange={(e) => setLandingAmount(parseInt(e.target.value) || 0)}
                  className="pl-8"
                  min="1000"
                  step="10000"
                  data-testid="landing-amount-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('admin_settings_landing_amount_desc')}</p>
            </div>
          </div>

          {/* Hero Text Editor */}
          <div className="mt-6 border-t border-border/50 pt-6">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="font-heading text-lg font-semibold text-primary">{t('admin_settings_hero_title')}</h3>
              <div className="flex gap-1">
                {['ja', 'pt', 'en'].map(lang => (
                  <button
                    key={lang}
                    onClick={() => setEditLang(lang)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${editLang === lang ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'}`}
                    data-testid={`hero-lang-${lang}`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3">
              <div>
                <Label className="text-xs">{t('admin_settings_hero_line1')}</Label>
                <Input
                  value={heroTexts[editLang]?.title_1 || ''}
                  onChange={(e) => setHeroTexts(prev => ({ ...prev, [editLang]: { ...prev[editLang], title_1: e.target.value } }))}
                  placeholder={editLang === 'ja' ? '日本からブラジルへ' : editLang === 'pt' ? 'Do Japao para o Brasil' : 'From Japan to Brazil'}
                  data-testid="hero-title1-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t('admin_settings_hero_line2')}</Label>
                <Input
                  value={heroTexts[editLang]?.title_2 || ''}
                  onChange={(e) => setHeroTexts(prev => ({ ...prev, [editLang]: { ...prev[editLang], title_2: e.target.value } }))}
                  placeholder={editLang === 'ja' ? '簡単・安心' : editLang === 'pt' ? 'Facil e Seguro' : 'Easy & Secure'}
                  data-testid="hero-title2-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t('admin_settings_hero_line3')}</Label>
                <Input
                  value={heroTexts[editLang]?.title_3 || ''}
                  onChange={(e) => setHeroTexts(prev => ({ ...prev, [editLang]: { ...prev[editLang], title_3: e.target.value } }))}
                  placeholder={editLang === 'ja' ? '送金' : editLang === 'pt' ? 'Remessa' : 'Remittance'}
                  data-testid="hero-title3-input"
                />
              </div>
              <div>
                <Label className="text-xs">{t('admin_settings_hero_desc')}</Label>
                <Input
                  value={heroTexts[editLang]?.desc || ''}
                  onChange={(e) => setHeroTexts(prev => ({ ...prev, [editLang]: { ...prev[editLang], desc: e.target.value } }))}
                  placeholder={editLang === 'ja' ? '最短3クリックで送金完了...' : editLang === 'pt' ? 'Envie dinheiro em 3 cliques...' : 'Send money in 3 clicks...'}
                  data-testid="hero-desc-input"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t('admin_settings_hero_hint')}</p>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleSaveLanding}
              disabled={savingLanding}
              size="sm"
              className="gap-2"
              data-testid="save-landing-btn"
            >
              <Save className="h-4 w-4" />
              {savingLanding ? t('admin_settings_saving') : t('admin_settings_save')}
            </Button>
          </div>
        </div>

        {/* Market Rate Section */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-8" data-testid="market-rate-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary">{t('settings_market_rate_title')}</h2>
              <p className="text-sm text-muted-foreground">{t('settings_market_rate_subtitle')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Current Rate */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-5 text-white" data-testid="current-rate-card">
              <div className="text-sm text-slate-400 mb-1">{t('settings_current_rate')}</div>
              <div className="text-3xl font-bold font-mono" data-testid="market-rate-value">
                {currentRate ? currentRate.toFixed(5) : '-'}
              </div>
              <div className="text-xs text-slate-400 mt-1">1 JPY = ? BRL</div>
              {isOverrideMode && (
                <span className="mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-500 text-black">
                  {t('settings_manual_mode')}
                </span>
              )}
              {!isOverrideMode && marketRate?.is_live && (
                <span className="mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500 text-white">
                  {t('settings_live_mode')}
                </span>
              )}
            </div>

            {/* Last Updated */}
            <div className="bg-secondary/30 rounded-xl p-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                {t('settings_last_updated')}
              </div>
              <div className="text-sm font-medium text-primary" data-testid="last-updated">
                {formatTime(marketRate?.fetched_at)}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {t('settings_source')}: {marketRate?.source === 'manual' ? t('settings_source_manual') : 'Frankfurter (ECB)'}
              </div>
            </div>

            {/* Next Refresh */}
            <div className="bg-secondary/30 rounded-xl p-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <Zap className="h-4 w-4" />
                {t('settings_auto_refresh')}
              </div>
              <div className="text-sm font-medium text-primary">
                {t('settings_every_3h')}
              </div>
              {marketRate?.next_refresh_at && !isOverrideMode && (
                <div className="text-xs text-muted-foreground mt-2" data-testid="next-refresh">
                  {t('settings_next_refresh')}: {formatTime(marketRate.next_refresh_at)}
                </div>
              )}
            </div>
          </div>

          {/* Rate Actions */}
          <div className="flex flex-wrap items-end gap-4 pt-4 border-t border-border/50">
            {/* Manual Refresh */}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleRefreshRate}
              disabled={refreshing}
              data-testid="refresh-rate-btn"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? t('settings_refreshing') : t('settings_refresh_now')}
            </Button>

            {/* Manual Override */}
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">{t('settings_manual_rate')}</Label>
                <Input
                  type="number"
                  step="0.00001"
                  min="0"
                  placeholder="0.03400"
                  value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)}
                  className="w-36 h-9 text-sm"
                  data-testid="manual-rate-input"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={handleOverrideRate}
                data-testid="override-rate-btn"
              >
                <Edit3 className="h-3.5 w-3.5" />
                {t('settings_override')}
              </Button>
            </div>

            {/* Re-enable Auto */}
            {isOverrideMode && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10"
                onClick={handleEnableAutoRate}
                data-testid="enable-auto-btn"
              >
                <RotateCcw className="h-4 w-4" />
                {t('settings_enable_auto')}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Fee Settings Form */}
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="fee-settings-form">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Settings className="h-5 w-5 text-accent" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">{t('settings_fee_title')}</h2>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="flat_fee">{t('admin_settings_flat_fee')}</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                  <Input
                    id="flat_fee"
                    type="number"
                    value={settings.flat_fee_jpy}
                    onChange={(e) => setSettings({...settings, flat_fee_jpy: parseFloat(e.target.value) || 0})}
                    className="pl-8"
                    min="0"
                    step="100"
                    data-testid="flat-fee-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t('settings_flat_fee_desc')}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="markup">{t('admin_settings_markup')}</Label>
                <div className="relative">
                  <Input
                    id="markup"
                    type="number"
                    value={settings.markup_percent}
                    onChange={(e) => setSettings({...settings, markup_percent: parseFloat(e.target.value) || 0})}
                    className="pr-8"
                    min="0"
                    max="20"
                    step="0.1"
                    data-testid="markup-input"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('settings_markup_desc')}</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button onClick={handleSave} disabled={saving} className="flex-1 gap-2" data-testid="save-settings-button">
                  <Save className="h-4 w-4" />
                  {saving ? t('admin_settings_saving') : t('admin_settings_save')}
                </Button>
                <Button variant="outline" onClick={handleReset} className="gap-2" data-testid="reset-settings-button">
                  <RefreshCw className="h-4 w-4" />
                  {t('admin_settings_reset')}
                </Button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-6">
            <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="fee-preview">
              <h2 className="font-heading text-xl font-semibold text-primary mb-6">
                {t('settings_simulation_title')}
              </h2>

              <div className="mb-5">
                <Label htmlFor="sim_amount" className="text-sm">{t('settings_sim_amount')}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                  <Input
                    id="sim_amount"
                    type="number"
                    value={simAmount}
                    onChange={(e) => setSimAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="pl-8 font-mono"
                    min="0"
                    step="10000"
                    data-testid="sim-amount-input"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">{t('settings_sim_amount')}</span>
                  <span className="font-mono font-medium text-primary">¥{preview.amount_jpy.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">{t('settings_sim_fee')}</span>
                  <span className="font-mono font-medium text-accent" data-testid="preview-fee">¥{preview.fee_jpy.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">{t('settings_sim_total')}</span>
                  <span className="font-mono font-bold text-primary text-lg" data-testid="preview-total">¥{preview.total_jpy.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">{t('settings_sim_market_rate')}</span>
                  <span className="font-mono text-sm">{currentRate ? currentRate.toFixed(5) : '-'} BRL/JPY</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">{t('settings_sim_applied_rate')}</span>
                  <span className="font-mono text-sm" data-testid="preview-rate">{preview.exchange_rate} BRL/JPY</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-accent/5 rounded-xl px-4">
                  <span className="text-muted-foreground font-medium">{t('settings_sim_receive')}</span>
                  <span className="font-mono font-bold text-accent text-2xl" data-testid="preview-brl">R$ {preview.amount_brl.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Revenue Info */}
            <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="revenue-info">
              <h3 className="font-heading text-lg font-semibold text-primary mb-4">{t('admin_settings_revenue')}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 px-3 bg-secondary/20 rounded-lg">
                  <span className="text-muted-foreground">{t('admin_settings_fee_per')}</span>
                  <span className="font-mono font-medium text-primary">¥{settings.flat_fee_jpy.toLocaleString()} {t('admin_settings_per_transfer')}</span>
                </div>
                <div className="flex justify-between py-2 px-3 bg-secondary/20 rounded-lg">
                  <span className="text-muted-foreground">{t('admin_settings_spread_per')}</span>
                  <span className="font-mono font-medium text-primary">{settings.markup_percent}% {t('admin_settings_per_transfer')}</span>
                </div>
                <div className="flex justify-between py-2 px-3 bg-accent/10 rounded-lg">
                  <span className="text-muted-foreground font-medium">{t('admin_settings_total_revenue')}</span>
                  <span className="font-mono font-bold text-accent">
                    ¥{(settings.flat_fee_jpy + simAmount * settings.markup_percent / 100).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Transfer Info */}
        <div className="mt-8 bg-card border border-border/50 rounded-2xl p-6" data-testid="bank-info-form">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary">{t('admin_settings_bank_title')}</h2>
              <p className="text-sm text-muted-foreground">{t('admin_settings_bank_subtitle')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="bank_name">{t('admin_settings_bank_name')}</Label>
              <Input id="bank_name" value={bankInfo.bank_name} onChange={(e) => setBankInfo({...bankInfo, bank_name: e.target.value})} placeholder="三菱UFJ銀行" data-testid="bank-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="branch_name">{t('admin_settings_branch_name')}</Label>
              <Input id="branch_name" value={bankInfo.branch_name} onChange={(e) => setBankInfo({...bankInfo, branch_name: e.target.value})} placeholder={t('admin_settings_branch_placeholder')} data-testid="branch-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account_type">{t('admin_settings_account_type')}</Label>
              <Input id="account_type" value={bankInfo.account_type} onChange={(e) => setBankInfo({...bankInfo, account_type: e.target.value})} placeholder={t('admin_settings_account_type_default')} data-testid="account-type-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account_number">{t('admin_settings_account_number')}</Label>
              <Input id="account_number" value={bankInfo.account_number} onChange={(e) => setBankInfo({...bankInfo, account_number: e.target.value})} placeholder="1234567" data-testid="account-number-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account_holder">{t('admin_settings_account_holder')}</Label>
              <Input id="account_holder" value={bankInfo.account_holder} onChange={(e) => setBankInfo({...bankInfo, account_holder: e.target.value})} placeholder="PRC Remit" data-testid="account-holder-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bank_notes">{t('admin_settings_bank_notes')}</Label>
              <Input id="bank_notes" value={bankInfo.notes} onChange={(e) => setBankInfo({...bankInfo, notes: e.target.value})} placeholder={t('admin_settings_notes_placeholder')} data-testid="bank-notes-input" />
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={handleSaveBankInfo} disabled={savingBank} className="gap-2" data-testid="save-bank-info-button">
              <Save className="h-4 w-4" />
              {savingBank ? t('admin_settings_saving') : t('admin_settings_bank_save')}
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
